import { FC, useContext } from "react";
import {
  InputGroup,
  Form,
  Button,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import RangeSlider from "react-bootstrap-range-slider";
import BasicModal from "../BasicModal";
import DotsLoader from "../DotsLoader";
import { FormContext } from "../FormContextProvider";
import InputField from "../inputField";
import { FormProps, UseType, VehicleClass } from "../types/form";
import { CURRENCY } from "../types/formConstants";
import { useCurrency } from "../useCurrency";
import useVehicleForm from "./useVehicleForm";

const VehicleForm: FC<FormProps> = ({ nextStep }) => {
  const {
    handleInsuranceAmountChange,
    handleVehicleChange,
    vehicleVersion,
    loadingPrice,
    vehicleModel,
    handleClose,
    vehicleMake,
    vehicleYear,
    isElectric,
    loading,
    isBike,
    formik,
    saving,
    amount,
    next,
    text,
    show,
  } = useVehicleForm(nextStep);
  const { resetContext } = useContext(FormContext);
  const { getCurrencyFormat } = useCurrency();

  return (
    <>
      <div className="mb-3 d-flex flex-column justify-content-center align-items-center">
        <div className="col-sm-6 col-12">
          {loading ? (
            <DotsLoader text="Cargando vehículos" />
          ) : (
            <>
              <InputGroup className="mb-2">
                <InputGroup.Text>Uso</InputGroup.Text>
                <Form.Select
                  name="type"
                  value={formik.values.type}
                  onChange={(e) => handleVehicleChange(e, "type")}
                >
                  {Object.entries(UseType).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <InputGroup className="mb-2">
                <InputGroup.Text>Clase Vehículo</InputGroup.Text>
                <Form.Select
                  name="class"
                  value={formik.values.class}
                  onChange={(e) => handleVehicleChange(e, "class")}
                >
                  {Object.entries(VehicleClass).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <InputGroup className="mb-2">
                <InputGroup.Text>Marca</InputGroup.Text>
                <Form.Select
                  name="make"
                  value={formik.values.make}
                  onChange={(e) => handleVehicleChange(e, "make")}
                >
                  {vehicleMake.map((make, key) => (
                    <option key={key} value={make.id}>
                      {make.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <InputGroup className="mb-2">
                <InputGroup.Text>Modelo</InputGroup.Text>
                <Form.Select
                  name="model"
                  value={formik.values.model}
                  onChange={(e) => handleVehicleChange(e, "model")}
                >
                  {vehicleModel.map((model, key) => (
                    <option key={key} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>

              <InputGroup className="mb-2">
                <InputGroup.Text>Versión</InputGroup.Text>
                <Form.Select
                  name="version"
                  value={formik.values.version}
                  onChange={(e) => handleVehicleChange(e, "version")}
                >
                  {vehicleVersion.map((version, key) => (
                    <option key={key} value={version.id}>
                      {version.name}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <InputGroup className="mb-2">
                <InputGroup.Text>Año</InputGroup.Text>
                <Form.Select
                  name="year"
                  value={formik.values.year}
                  onChange={(e) => handleVehicleChange(e, "year")}
                >
                  {vehicleYear.map((year, key) => (
                    <option key={key} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
              <div className="d-flex flex-lg-row flex-column justify-content-between">
                <div className="col-lg-4 col-12 mb-2">
                  <InputGroup>
                    <InputGroup.Text>Moneda</InputGroup.Text>
                    <Form.Select
                      disabled={isElectric(formik.values.version)}
                      name="currency"
                      value={formik.values.currency}
                      onChange={(e) => handleVehicleChange(e, "currency")}
                    >
                      {Object.values(CURRENCY).map((currency, key) => (
                        <option key={key} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </div>
                <div className="col-lg-7 col-12 mb-2 d-flex flex-row align-items-center">
                  <InputField
                    className="w-100"
                    type="number"
                    name="insuranceAmount"
                    formik={formik}
                    label="Valor del vehículo"
                  />
                  {isBike() && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>{`Monto mínimo: ${getCurrencyFormat(
                          amount.min,
                          formik.values.currency
                        )} Monto máximo: ${getCurrencyFormat(
                          amount.max,
                          formik.values.currency
                        )}`}</Tooltip>
                      }
                    >
                      <span className="bi bi-info-circle ms-1"></span>
                    </OverlayTrigger>
                  )}
                </div>
              </div>
              {loadingPrice ? (
                <Spinner className="ms-2" animation="border" size="sm" />
              ) : (
                !isBike() && (
                  <>
                    <RangeSlider
                      value={formik.values.insuranceAmount}
                      onChange={(e) => handleInsuranceAmountChange(e)}
                      min={amount.min}
                      max={amount.max}
                    />
                    <div className="d-flex flex-row justify-content-between">
                      <label>
                        Monto mínimo:{" "}
                        {getCurrencyFormat(amount.min, formik.values.currency)}
                      </label>
                      <label>
                        Monto máximo:{" "}
                        {getCurrencyFormat(amount.max, formik.values.currency)}
                      </label>
                    </div>
                  </>
                )
              )}
              <div className="w-100 d-flex flex-row justify-content-end my-3">
                <Button
                  onClick={next}
                  className="btn-width"
                  disabled={
                    saving ||
                    formik.values.insuranceAmount < amount.min ||
                    formik.values.insuranceAmount > amount.max ||
                    loadingPrice
                  }
                >
                  Siguiente
                  {saving && (
                    <Spinner className="ms-2" animation="border" size="sm" />
                  )}
                </Button>
              </div>
              <div className="d-flex flex-column justify-content-center align-items-center">
                <hr className="w-100" />
                <Button
                  className="btn-width"
                  onClick={resetContext}
                  variant="secondary"
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <BasicModal show={show} handleClose={handleClose} text={text} />
    </>
  );
};

export default VehicleForm;
